/**
 * Firmio Voice — the browser side of a GPT-Live session.
 *
 * Lifecycle: idle → requesting-mic → connecting → live → ending → ended | error.
 *
 *   1. getUserMedia (microphone), RTCPeerConnection, add the mic track,
 *      create the "oai-events" data channel BEFORE the offer (the channel has
 *      to be in the SDP).
 *   2. POST the offer to /api/voice-assistant/sessions; the backend creates the
 *      OpenAI session, attaches its sideband and returns the SDP answer.
 *   3. Remote audio plays in an <audio> element created inside the click
 *      handler (Safari only allows autoplay from a user gesture).
 *   4. The data channel streams the same JSON events the backend sees; here
 *      they only drive the UI: captions, "se gândește / caută" activity,
 *      elapsed seconds, and the close.
 *
 * The tools never run here — the API key and the DB stay on the server.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../lib/api';
import {
  closeVoiceSession,
  createVoiceSession,
  type VoiceSessionCreated,
} from '../lib/voiceAssistantApi';

export type VoiceAssistantPhase =
  | 'idle'
  | 'requesting-mic'
  | 'connecting'
  | 'live'
  | 'ending'
  | 'ended'
  | 'error';

export type VoiceActivity = 'idle' | 'thinking' | 'tool';

export interface VoiceCaption {
  id: number;
  speaker: 'user' | 'assistant';
  text: string;
  startMs: number;
  endMs: number;
}

export type VoiceErrorKind =
  | 'mic-denied'
  | 'mic-unavailable'
  | 'not-configured'
  | 'no-credits'
  | 'budget'
  | 'busy'
  | 'network'
  | 'generic';

export interface VoiceAssistantState {
  phase: VoiceAssistantPhase;
  activity: VoiceActivity;
  /** Tool currently running on the server, when known (for the activity chip). */
  activeTool: string | null;
  captions: VoiceCaption[];
  /** Seconds of session audio, as OpenAI reports them (~1/min) or locally between ticks. */
  seconds: number;
  muted: boolean;
  session: VoiceSessionCreated | null;
  error: VoiceErrorKind | null;
  /** Why the session ended, from OpenAI (`close_requested`, `expired`, …). */
  closeReason: string | null;
  /** Drafts the assistant proposed (tasks / reminders / meetings) awaiting approval in the chat. */
  pendingSuggestions: number;
}

const ICE_GATHER_TIMEOUT_MS = 2000;
/** Fragments of the same speaker closer than this merge into one caption line. */
const CAPTION_MERGE_GAP_MS = 4000;

function classifyError(err: unknown): VoiceErrorKind {
  const name = err instanceof Error ? err.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'mic-denied';
  if (name === 'NotFoundError' || name === 'NotReadableError' || name === 'OverconstrainedError') {
    return 'mic-unavailable';
  }
  if (err instanceof ApiError) {
    // 503 used to mean only "no OpenAI key on the server"; the same status now
    // also carries an exhausted OpenAI account and a rate limit, which are not
    // configuration problems and must not be reported as one.
    if (err.code === 'OPENAI_NO_CREDITS') return 'no-credits';
    if (err.code === 'OPENAI_RATE_LIMITED') return 'network';
    if (err.status === 503) return 'not-configured';
    if (err.code === 'AI_BUDGET_EXCEEDED') return 'budget';
    if (err.code === 'VOICE_SESSIONS_LIMIT') return 'busy';
    if (err.status === 502 || err.status === 429) return 'network';
  }
  return 'generic';
}

/** Wait for ICE candidates so the offer carries them (with a cap; trickle is fine too). */
function waitForIceGathering(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', check);
      resolve();
    };
    const check = () => {
      if (pc.iceGatheringState === 'complete') done();
    };
    pc.addEventListener('icegatheringstatechange', check);
    setTimeout(done, ICE_GATHER_TIMEOUT_MS);
  });
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

export function useVoiceAssistant() {
  const [state, setState] = useState<VoiceAssistantState>({
    phase: 'idle',
    activity: 'idle',
    activeTool: null,
    captions: [],
    seconds: 0,
    muted: false,
    session: null,
    error: null,
    closeReason: null,
    pendingSuggestions: 0,
  });

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sessionRef = useRef<VoiceSessionCreated | null>(null);
  const captionIdRef = useRef(0);
  const startedAtRef = useRef<number | null>(null);
  const reportedSecondsRef = useRef(0);
  const tickRef = useRef<number | null>(null);
  const endingRef = useRef(false);

  const patch = useCallback((p: Partial<VoiceAssistantState>) => {
    setState((s) => ({ ...s, ...p }));
  }, []);

  const teardownMedia = useCallback(() => {
    if (tickRef.current != null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    try {
      dcRef.current?.close();
    } catch {
      // ignore
    }
    dcRef.current = null;
    delete (window as unknown as { __firmioVoice?: unknown }).__firmioVoice;
    try {
      pcRef.current?.close();
    } catch {
      // ignore
    }
    pcRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.srcObject = null;
      audioRef.current.remove();
      audioRef.current = null;
    }
  }, []);

  const appendCaption = useCallback(
    (speaker: 'user' | 'assistant', ev: Record<string, unknown>) => {
      const text = typeof ev.delta === 'string' ? ev.delta.trim() : '';
      if (!text) return;
      const startMs = num(ev.start_ms);
      const endMs = num(ev.end_ms);
      setState((s) => {
        const last = s.captions[s.captions.length - 1];
        if (last && last.speaker === speaker && startMs - lastEnd(last) <= CAPTION_MERGE_GAP_MS) {
          const merged = { ...last, text: `${last.text} ${text}`, endMs };
          return { ...s, captions: [...s.captions.slice(0, -1), merged] };
        }
        captionIdRef.current += 1;
        return {
          ...s,
          captions: [...s.captions, { id: captionIdRef.current, speaker, text, startMs, endMs }],
        };
      });
    },
    [],
  );

  const finish = useCallback(
    (closeReason: string | null, error: VoiceErrorKind | null = null) => {
      teardownMedia();
      const s = sessionRef.current;
      sessionRef.current = null;
      if (s && !endingRef.current) {
        // The server may already know (session.closed reached its sideband);
        // the close is idempotent there, so this is just belt and braces.
        void closeVoiceSession(s.sessionId)
          .then((closed) => patch({ pendingSuggestions: closed.pendingSuggestions ?? 0 }))
          .catch(() => undefined);
      }
      endingRef.current = false;
      patch({
        phase: error ? 'error' : 'ended',
        activity: 'idle',
        activeTool: null,
        closeReason,
        error,
      });
    },
    [patch, teardownMedia],
  );

  const handleEvent = useCallback(
    (ev: Record<string, unknown>) => {
      switch (ev.type) {
        case 'session.started':
          startedAtRef.current = Date.now();
          reportedSecondsRef.current = 0;
          patch({ phase: 'live' });
          return;
        case 'session.input_transcript.delta':
          appendCaption('user', ev);
          return;
        case 'session.output_transcript.delta':
          appendCaption('assistant', ev);
          return;
        case 'session.delegation.created':
          patch({ activity: 'thinking', activeTool: null });
          return;
        case 'response.event': {
          const inner = (ev.event ?? {}) as Record<string, unknown>;
          if (inner.type === 'response.output_item.done') {
            const item = (inner.item ?? {}) as Record<string, unknown>;
            if (item.type === 'function_call') {
              patch({
                activity: 'tool',
                activeTool: typeof item.name === 'string' ? item.name : null,
              });
            }
          } else if (inner.type === 'response.completed' || inner.type === 'response.done') {
            setState((s) =>
              s.activity === 'tool' ? s : { ...s, activity: 'idle', activeTool: null },
            );
          } else if (inner.type === 'response.output_text.delta') {
            setState((s) =>
              s.activity === 'thinking' ? s : { ...s, activity: 'thinking', activeTool: null },
            );
          }
          return;
        }
        case 'session.usage.updated': {
          const usage = (ev.usage ?? {}) as Record<string, unknown>;
          const seconds = num(usage.seconds);
          if (seconds > reportedSecondsRef.current) {
            reportedSecondsRef.current = seconds;
            startedAtRef.current = Date.now() - seconds * 1000;
            patch({ seconds });
          }
          return;
        }
        case 'session.closed': {
          const usage = (ev.usage ?? {}) as Record<string, unknown>;
          const seconds = num(usage.seconds);
          if (seconds) patch({ seconds });
          finish(typeof ev.reason === 'string' ? ev.reason : 'closed');
          return;
        }
        case 'error': {
          const error = (ev.error ?? {}) as Record<string, unknown>;
          // Command errors do not end the session; a startup error does, and
          // in that case session.started never comes → the connecting state
          // would hang, so surface it.
          if (startedAtRef.current == null) {
            finish(null, 'generic');
          } else {
            console.warn('[voice] GPT-Live error', error);
          }
          return;
        }
        default:
          return;
      }
    },
    [appendCaption, finish, patch],
  );

  const start = useCallback(async () => {
    if (pcRef.current) return;
    endingRef.current = false;
    captionIdRef.current = 0;
    startedAtRef.current = null;
    reportedSecondsRef.current = 0;
    patch({
      phase: 'requesting-mic',
      activity: 'idle',
      activeTool: null,
      captions: [],
      seconds: 0,
      muted: false,
      session: null,
      error: null,
      closeReason: null,
      pendingSuggestions: 0,
    });

    // Created synchronously in the click handler so autoplay is allowed.
    const audio = document.createElement('audio');
    audio.autoplay = true;
    audio.setAttribute('playsinline', 'true');
    audio.style.display = 'none';
    document.body.appendChild(audio);
    audioRef.current = audio;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      patch({ phase: 'connecting' });

      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.addEventListener('track', (event) => {
        if (audioRef.current) {
          audioRef.current.srcObject = new MediaStream([event.track]);
          void audioRef.current.play().catch(() => undefined);
        }
      });
      pc.addEventListener('connectionstatechange', () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
          if (pcRef.current === pc && !endingRef.current) finish('connection_lost', 'network');
        }
      });
      for (const track of stream.getAudioTracks()) pc.addTrack(track, stream);

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;
      dc.addEventListener('message', (event) => {
        try {
          const parsed: unknown = JSON.parse(String(event.data));
          if (parsed && typeof parsed === 'object') {
            const ev = parsed as Record<string, unknown>;
            // Trace everything but the transcript fragments: the GPT-Live
            // event shapes are what a stuck session has to be debugged from.
            if (typeof ev.type === 'string' && !ev.type.endsWith('transcript.delta')) {
              console.debug('[voice] <-', ev.type, ev);
            }
            handleEvent(ev);
          }
        } catch {
          // ignore non-JSON frames
        }
      });
      // Debug hook (devtools): `__firmioVoice.send({...})` injects a client
      // event into the live session, e.g. a typed user message for the backend
      // when no microphone is at hand. Removed on teardown.
      (window as unknown as { __firmioVoice?: unknown }).__firmioVoice = {
        send: (ev: Record<string, unknown>) => {
          console.debug('[voice] ->', ev.type, ev);
          dc.send(JSON.stringify(ev));
        },
        ask: (text: string) => {
          dc.send(
            JSON.stringify({
              type: 'response.item.create',
              item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] },
            }),
          );
          dc.send(JSON.stringify({ type: 'response.create' }));
        },
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitForIceGathering(pc);
      const localSdp = pc.localDescription?.sdp ?? offer.sdp ?? '';

      const session = await createVoiceSession(localSdp);
      sessionRef.current = session;
      patch({ session });
      await pc.setRemoteDescription({ type: 'answer', sdp: session.sdp });

      // Local clock between OpenAI's ~1/min usage ticks.
      tickRef.current = window.setInterval(() => {
        if (startedAtRef.current == null) return;
        const seconds = Math.floor((Date.now() - startedAtRef.current) / 1000);
        setState((s) => (seconds > s.seconds ? { ...s, seconds } : s));
      }, 1000);
    } catch (err) {
      console.warn('[voice] start failed', err);
      const kind = classifyError(err);
      teardownMedia();
      sessionRef.current = null;
      patch({ phase: 'error', error: kind, session: null });
    }
  }, [finish, handleEvent, patch, teardownMedia]);

  const end = useCallback(async () => {
    const s = sessionRef.current;
    if (!s) {
      teardownMedia();
      patch({ phase: 'idle' });
      return;
    }
    endingRef.current = true;
    patch({ phase: 'ending' });
    try {
      dcRef.current?.send(JSON.stringify({ type: 'session.close' }));
    } catch {
      // ignore
    }
    let pendingSuggestions = 0;
    try {
      const closed = await closeVoiceSession(s.sessionId, 'close_requested');
      pendingSuggestions = closed.pendingSuggestions ?? 0;
    } catch {
      // The sideband may have finalised it already.
    }
    sessionRef.current = null;
    teardownMedia();
    endingRef.current = false;
    patch({
      phase: 'ended',
      activity: 'idle',
      activeTool: null,
      closeReason: 'close_requested',
      pendingSuggestions,
    });
  }, [patch, teardownMedia]);

  const toggleMute = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    setState((s) => {
      const muted = !s.muted;
      stream.getAudioTracks().forEach((t) => {
        t.enabled = !muted;
      });
      return { ...s, muted };
    });
  }, []);

  const reset = useCallback(() => {
    teardownMedia();
    sessionRef.current = null;
    patch({
      phase: 'idle',
      error: null,
      closeReason: null,
      captions: [],
      seconds: 0,
      session: null,
    });
  }, [patch, teardownMedia]);

  // Unmount: stop the mic and tell the server, so the session does not run on.
  useEffect(() => {
    return () => {
      const s = sessionRef.current;
      sessionRef.current = null;
      teardownMedia();
      if (s) void closeVoiceSession(s.sessionId, 'close_requested').catch(() => undefined);
    };
  }, [teardownMedia]);

  return { ...state, start, end, toggleMute, reset };
}

function lastEnd(caption: VoiceCaption): number {
  return caption.endMs || caption.startMs;
}
